import type { RuleEdge } from "../types/edges";
import type { LoopNodeData, RuleNode } from "../types/nodes";
import type { Transition } from "../types/rule-lifecycle";
import { loopEdgesToSaveTransitions } from "./rule-transitions-utils";

export type LoopScopeValidationError = { message: string };

const clearLoopNodeId = (node: RuleNode): RuleNode => {
	const next = { ...node };
	delete next.loopNodeId;
	const data = { ...node.data } as Record<string, unknown>;
	delete data.loopNodeId;
	return { ...next, data } as RuleNode;
};

/**
 * Strips loop scope from top-level canvas nodes. Loop bodies keep their explicit
 * `loopNodeId` (set during hydration) untouched, since scope is authoritative there.
 */
export const stripTopLevelLoopNodeIds = (nodes: RuleNode[]): RuleNode[] =>
	nodes.map((n) => {
		if (n.type === "loop") {
			const ld = n.data as LoopNodeData;
			return {
				...clearLoopNodeId(n),
				data: {
					...ld,
					loopNodes: ld.loopNodes ?? [],
					loopEdges: ld.loopEdges ?? [],
				},
			} as RuleNode;
		}
		return clearLoopNodeId(n);
	});

type SerializeChildNode = (node: RuleNode, parentLoopId: string) => Record<string, unknown>;

/**
 * Recursively builds `loopNodes` / `loopEdges` for save with explicit `loopNodeId` on every
 * scoped node and inner transition. Nested loops keep their own subtree under `data`.
 */
export const serializeLoopBodyForSave = (
	owningLoopId: string,
	loopNodes: RuleNode[],
	loopEdges: RuleEdge[],
	mapChildNode: SerializeChildNode,
	parentLoopEdges?: RuleEdge[],
): { loopNodes: Record<string, unknown>[]; loopEdges: Transition[] } => {
	const serializedNodes = (loopNodes ?? []).map((child) => {
		const mapped = mapChildNode(child, owningLoopId);
		if (child.type === "loop") {
			const ld = child.data as LoopNodeData;
			const nested = serializeLoopBodyForSave(
				child.id,
				ld.loopNodes ?? [],
				ld.loopEdges ?? [],
				mapChildNode,
				loopEdges,
			);
			return {
				...mapped,
				loopNodeId: owningLoopId,
				data: {
					...(mapped.data as Record<string, unknown>),
					loopNodes: nested.loopNodes,
					loopEdges: nested.loopEdges,
				},
			};
		}
		return {
			...mapped,
			loopNodeId: owningLoopId,
			data: {
				...(mapped.data as Record<string, unknown>),
				loopNodeId: owningLoopId,
			},
		};
	});

	const loopEdgeTransitions = loopEdgesToSaveTransitions(
		owningLoopId,
		loopEdges ?? [],
		loopNodes ?? [],
		parentLoopEdges != null ? { parentLoopId: owningLoopId, parentLoopEdges } : undefined,
	);

	return { loopNodes: serializedNodes, loopEdges: loopEdgeTransitions };
};

function validateSerializedLoopBody(
	owningLoopId: string,
	loopNodes: unknown[],
	loopEdges: Transition[],
	errors: LoopScopeValidationError[],
): void {
	for (const raw of loopNodes ?? []) {
		if (raw == null || typeof raw !== "object") continue;
		const child = raw as { id?: unknown; loopNodeId?: unknown; type?: string; data?: LoopNodeData };
		if (child.loopNodeId == null || String(child.loopNodeId) !== String(owningLoopId)) {
			errors.push({
				message: `Node ${child.id} in loop ${owningLoopId} must have loopNodeId ${owningLoopId}`,
			});
		}
		if (child.type === "loop" && child.data) {
			validateSerializedLoopBody(
				String(child.id),
				(child.data.loopNodes as unknown[]) ?? [],
				(child.data.loopEdges as unknown as Transition[]) ?? [],
				errors,
			);
		}
	}
	for (const t of loopEdges ?? []) {
		if (t.loopNodeId == null || String(t.loopNodeId) !== String(owningLoopId)) {
			errors.push({
				message: `Inner transition ${t.from}→${t.to} in loop ${owningLoopId} must have loopNodeId ${owningLoopId}`,
			});
		}
	}
}

/** Client-side scope checks on the serializable save payload. */
export const validateSerializableLoopScopes = (
	topLevelNodes: Record<string, unknown>[],
	outerTransitions: Transition[],
): LoopScopeValidationError[] => {
	const errors: LoopScopeValidationError[] = [];

	for (const t of outerTransitions) {
		if (t.loopNodeId != null) {
			errors.push({ message: `Outer transition ${t.from}→${t.to} must not have loopNodeId` });
		}
	}

	for (const raw of topLevelNodes) {
		if (raw.loopNodeId != null) {
			errors.push({ message: `Top-level node ${raw.id} must not have loopNodeId` });
		}
		if (raw.type !== "loop") continue;
		const data = raw.data as LoopNodeData | undefined;
		if (!data) continue;
		validateSerializedLoopBody(
			String(raw.id),
			(data.loopNodes as unknown[]) ?? [],
			(data.loopEdges as unknown as Transition[]) ?? [],
			errors,
		);
	}

	return errors;
};
